/**
 * InfoTooltipWrapper — Feb 2026, upgraded Jun 2026 ("Maya Explains").
 *
 * Wraps any dashboard element with a hover-reveal Maya affordance: hovering
 * still shows the quick tooltip (title + description), and CLICKING the icon
 * opens the MayaExplainBubble — Maya explains the feature in full
 * (What it is · What it does · How to use it) from the Maya Explains CMS.
 *
 * Non-intrusive: the button only appears on hover and never intercepts
 * underlying card interactions.
 */
import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import MayaExplainBubble from './MayaExplainBubble';

const InfoTooltipWrapper = ({ title, description, children, testId, marginBottom = 'mb-6', id, className = '', persistent = false }) => {
  const [open, setOpen] = useState(false);
  if (!title && !description) return <div id={id} className={`${marginBottom} ${className}`.trim()}>{children}</div>;
  const idSuffix = (testId || (title || 'item').toLowerCase().replace(/[^a-z0-9]+/g, '-')).slice(0, 60);
  return (
    <div id={id} className={`relative group ${marginBottom} ${className}`.trim()} data-testid={`info-wrap-${idSuffix}`}>
      <div className={`absolute -top-2 right-3 z-20 transition-opacity ${persistent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <div className="relative group/info">
          <button
            type="button"
            className="p-1 min-w-[40px] min-h-[40px] text-white rounded-full shadow-md flex items-center justify-center hover:scale-110 transition-transform"
            style={{ background: 'linear-gradient(135deg,#F5831F 0%,#FF6B35 70%)' }}
            aria-label={`Ask Maya about ${title || 'this section'}`}
            data-testid={`info-btn-${idSuffix}`}
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          >
            <Sparkles size={12} />
          </button>
          <div
            className="absolute right-0 top-full mt-1 w-72 rounded-lg bg-gray-900 text-white text-xs leading-snug p-3 shadow-xl opacity-0 group-hover/info:opacity-100 group-focus-within/info:opacity-100 transition-opacity pointer-events-none z-30"
            role="tooltip"
            data-testid={`info-tooltip-${idSuffix}`}
          >
            {title && <div className="font-bold text-orange-300 mb-1">{title}</div>}
            {description && <div className="text-gray-100 whitespace-pre-line">{description}</div>}
            <div className="mt-1.5 text-[10px] text-orange-200/80">Tap the icon and Maya explains it fully</div>
          </div>
        </div>
      </div>
      {open && (
        <MayaExplainBubble
          query={title}
          fallbackTitle={title}
          fallbackDescription={description}
          onClose={() => setOpen(false)}
        />
      )}
      {children}
    </div>
  );
};

export default InfoTooltipWrapper;
